import QuillEditor from '../components/input/QuillEditor'

import { getMidnightTimestampFromTimestamp } from '../mixins/timestampManipulation'
import { CoachAssignment, ProgramPlan } from '../structs/structs'
import { APIError } from '../helpers'

import InputHeader from '../components/InputHeader'
import InputGroup from '../components/InputGroup'
import TextInput from '../components/input/TextInput'
import DateInput from '../components/input/DateInput'
import DropdownInput from '../components/input/DropdownInput'
import AsyncDropdownInput from '../components/input/AsyncDropdownInput'
import TextAreaInput from '../components/input/TextAreaInput'
import NumberInput from '../components/input/NumberInput'

const patientObjectiveOptions = [
  {
    value: 'Reduce monthly migraine days',
    label: 'patients.profile.objectives.days'
  },
  {
    value: 'Reduce migraine pain intensity',
    label: 'patients.profile.objectives.intensity'
  },
  {
    value: 'Have shorter migraines',
    label: 'patients.profile.objectives.duration'
  }
]

// eslint-disable-next-line no-undef
export const getProgramPlans = (axiosInstance = app.__vue__.$axios, requestConfig = app.__vue__.requestConfig) => {
  return new Promise(
    (resolve, reject) => {
      const requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programPlanTemplates?limit=10000'

      axiosInstance.get(
        requestUrl,
        requestConfig
      ).then(
        ({ data }) => {
          let programPlans = data.result.map(programPlan => new ProgramPlan(programPlan))
          resolve(programPlans)
        },
        ({ response }) => {
          reject(new APIError('Error fetching program plan templates', response))
        }
      )
    }
  )
}

export const getProgramPlanOptions = async programPlanType => {
  let programPlans = await getProgramPlans()

  let options = (
    programPlans
      .filter(
        coachingPlan => coachingPlan.planType === programPlanType
      )
      .map(
        coachingPlan => (
          {
            untranslated: coachingPlan.data.title,
            value: coachingPlan.id
          }
        )
      )
  )

  return options
}

export const buttonIconOptions = [
  {
    label: 'lessonForm.icons.noIcon',
    value: null
  },
  {
    label: 'lessonForm.icons.download',
    value: 'download'
  },
  {
    label: 'lessonForm.icons.watch',
    value: 'watch'
  },
  {
    label: 'lessonForm.icons.read',
    value: 'read'
  }
]

export const patientLang = {
  component: DropdownInput,
  data: {
    label: 'patients.profile.language',
    placeholder: 'patients.profile.language',
    required: true,
    options: [
      {
        value: 'en',
        label: 'english'
      },
      {
        value: 'de',
        label: 'german'
      }
    ]
  }
}

// eslint-disable-next-line no-undef
export const getCoaches = (axiosInstance = app.__vue__.$axios, requestConfig = app.__vue__.requestConfig, storeInstance = app.__vue__.$store) => {
  // // FIXME: Remove this after backend allows coaches to fetch coaches
  return new Promise(
    (resolve, reject) => {
      if (Array.isArray(storeInstance.state.coaches) && storeInstance.state.coaches.length) {
        resolve(storeInstance.state.coaches)
      } else {
        let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/coaches?limit=10000'

        axiosInstance.get(
          requestUrl,
          requestConfig
        ).then(
          ({ data }) => {
            resolve(data.result.result)
          },
          ({ response }) => {
            reject(new APIError('Error fetching program coaches', response))
          }
        )
      }
    }
  )
}

export const getCoachOptions = async () => {
  let coaches = await getCoaches()

  let filteredCoaches = (
    coaches
      .filter(
        coach => (
          process.env.NODE_ENV === 'production'
            ? coach.email && !coach.email.includes('@healint.com')
            : coach.email
        )
      )
      .map(
        coach => (
          {
            untranslated: coach.email,
            value: coach.id
          }
        )
      )
  )

  return filteredCoaches
}

export const constructCoachAssignment = coachId => {
  return (
    new CoachAssignment(
      {
        coachId: coachId,
        // eslint-disable-next-line no-undef
        assignedBy: app.__vue__.$store.state.userEmail,
        assignmentTime: Date.now()
      }
    )
  )
}

export const shouldSetDefaultForCheckboxInput = componentInstance => componentInstance.$route.name.includes('Create')

export const externalId = {
  component: TextInput,
  data: {
    label: 'medgateId',
    placeholder: 'medgateId',
    required: true
  }
}

export const startDate = (disabled = true, hasDefault = false) => (
  {
    startDate: {
      component: DateInput,
      data: {
        disabled,
        label: 'patients.profile.startDate',
        placeholder: 'patients.profile.startDate',
        ...(hasDefault ? { default: getMidnightTimestampFromTimestamp(Date.now()) } : {})
      }
    }
  }
)

export const employer = {
  component: DropdownInput,
  data: {
    label: 'employer',
    placeholder: 'employer',
    required: true,
    options: [
      {
        value: 'novartis',
        label: 'employers.novartis'
      },
      {
        value: 'axa',
        label: 'employers.axa'
      },
      {
        value: 'swisscom',
        label: 'employers.swisscom'
      },
      {
        value: 'biogen',
        label: 'employers.biogen'
      }
    ]
  }
}

export const name = {
  component: TextInput,
  data: {
    label: 'patientName',
    placeholder: 'patientName',
    required: true
  }
}

export const coachNotes = {
  component: QuillEditor,
  data: {
    label: 'patients.profile.notes',
    placeholder: 'patients.profile.notesPlace'
  }
}

export const language = {
  component: TextInput,
  data: {
    label: 'language',
    placeholder: 'language',
    disabled: true
  }
}

export const objective = {
  component: DropdownInput,
  data: {
    default: [],
    label: 'patients.profile.objective',
    placeholder: 'patients.profile.objective',
    multiple: false,
    required: true,
    options: patientObjectiveOptions
  }
}

export const currentCoachFields = () => (
  {
    currentCoachHeader: {
      component: InputHeader,
      data: {
        header: 'patients.profile.coach',
        subheader: 'patients.profile.coachPlace',
        icon: 'user md icon'
      }
    },
    currentCoach: {
      component: AsyncDropdownInput,
      data: {
        default: [],
        label: 'patients.profile.coach',
        placeholder: 'patients.profile.coachPlace',
        multiple: false,
        required: true,
        options: [],
        checkFields: ['coachId', 'assignedBy', 'assignmentTime'],
        errorMessage: 'login.unableToFetchCoaches'
      },
      functions: {
        async getOptions () {
          return getCoachOptions()
        },
        constructInput (option) {
          return constructCoachAssignment(option)
        },
        mapInputToOption (options, input) {
          let selectedOption = options.find(option => option.value === input.coachId)
          let hasSelectedOption = !!selectedOption

          return (
            hasSelectedOption
              ? selectedOption.value
              : null
          )
        }
      }
    }
  }
)

export const reminderHeader = {
  component: InputHeader,
  data: {
    header: 'actionForm.reminderHeader.header',
    subheader: 'actionForm.reminderHeader.subheader',
    icon: 'bell outline'
  }
}

export const reminder = {
  component: InputGroup,
  data: {
    title: {
      component: TextInput,
      data: {
        name: 'reminder-title',
        label: 'actionForm.reminderTitle',
        placeholder: 'actionForm.reminderTitlePlace',
        required: true
      }
    },
    body: {
      component: TextAreaInput,
      data: {
        name: 'reminder-body',
        label: 'actionForm.reminderBody',
        required: true,
        autofocus: false,
        rows: 2,
        placeholder: 'actionForm.reminderBodyPlace',
        disabled: false
      }
    }
  }
}

export const actionContentFields = () => (
  {
    contentHeader: {
      component: InputHeader,
      data: {
        header: 'actionForm.contentHeader.header',
        subheader: 'actionForm.contentHeader.subheader',
        icon: 'mobile alternate'
      }
    },
    title: {
      component: TextInput,
      data: {
        name: 'title',
        label: 'actionForm.title',
        placeholder: 'actionForm.title',
        required: true
      }
    },
    body: {
      component: QuillEditor,
      data: {
        label: 'actionForm.body',
        placeholder: 'actionForm.bodyPlace'
      }
    }
  }
)

export const lessonContentFields = () => (
  {
    contentHeader: {
      component: InputHeader,
      data: {
        header: 'lessonForm.contentHeader.header',
        subheader: 'lessonForm.contentHeader.subheader',
        icon: 'mobile alternate'
      }
    },
    title: {
      component: TextInput,
      data: {
        name: 'title',
        label: 'lessonForm.title',
        placeholder: 'lessonForm.title',
        required: true
      }
    },
    body: {
      component: QuillEditor,
      data: {
        label: 'lessonForm.body',
        placeholder: 'lessonForm.bodyPlace'
      }
    }
  }
)

export const lessonStartDayHeader = () => (
  {
    startDayHeader: {
      component: InputHeader,
      data: {
        header: 'lessonForm.startDayHeader.header',
        subheader: 'lessonForm.startDayHeader.subheader',
        icon: 'calendar outline alternate'
      }
    }
  }
)

export const startDay = (required = true, disabled = false, defaultValue = 1) => (
  {
    startDay: {
      component: NumberInput,
      data: {
        name: 'start-day',
        label: 'startDay',
        placeholder: 'startDay',
        required,
        disabled,
        default: defaultValue
      }
    }
  }
)
export const endDay = (required = true, disabled = false, defaultValue = 14) => (
  {
    endDay: {
      component: NumberInput,
      data: {
        name: 'end-day',
        label: 'endDay',
        placeholder: 'endDay',
        required,
        disabled,
        default: defaultValue
      }
    }
  }
)
